<script lang="ts">
	import { devices_status } from 'src/stores/device-runtime/status';
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Design system components
	import {
		Alert,
		toast,
		Label,
		MediaPlayer,
		DashMediaPlayerWidget,
	} from '@Shelly/shelly-design-system';
	//Helpers
	import { get_ip, real_id } from 'src/helpers/interface_helpers';
	import {
		openExternalLink,
		percent_from_value_range,
		value_from_percent_range,
	} from 'src/helpers/utilities';
	import { setRadioFavoritesCache } from 'src/components/Scenes/helpers/radio_favorites_cache';
	//Types
	import type { Device } from 'src/types/Device';
	import type { MediaResponseInterface } from 'src/types/MediaResponseInterface';
	//Svelte Hooks
	import { beforeUpdate, onDestroy, onMount } from 'svelte';
	//Components
	import { hapticFeedback } from 'src/services/native_container_requests_ios';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { os } from 'src/stores/session/native-bridge';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	import PinUnlock from './PinUnlock.svelte';
	import { unlockPin } from 'src/helpers/pin_lock_helpers';
	import { LinkWithIcon } from '@Shelly/shelly-design-system';

	export let device: Device;
	export let dashWidgetVariant = false;
	export let mediaWidget;

	interface mediaFilesInterface {
		id: number;
		filename: string;
		thumb: string;
		title: string;
		duration: number;
		size: number;
		type: string;
		valid: boolean;
		index: number;
		track: number;
		artist: string;
		album: string;
		year: number;
		preview: string;
	}

	$: statusStore = getDeviceStatus(device.id);
	$: status = $statusStore;

	const channel = device?.shared ? device?.channel : undefined; //well this is needed for controlling the shared devices
	let mediaFiles: mediaFilesInterface[] = [
		// {
		// 	id: 1,
		// 	filename: 'babyshark.mp3',
		// 	preview: 'http://192.168.7.173:8021/getMediaThumb?id=1&_t=babyshark.mp3',
		// 	track: 0,
		// 	artist: 'Unknown Artist',
		// 	album: 'Unknown Album',
		// 	year: 0,
		// 	duration: 294568,
		// 	title: 'babyshark.mp3',
		// 	size: 4767981,
		// 	index: 0,
		// 	type: 'AUDIO',
		// 	valid: true
		// }
	];

	let inputSearchValue = '';
	let currentSong = null;
	let currentRadio = null;

	$: noResultsFound = {
		icon: 'fa-light fa-magnifying-glass',
		title: $format('no_results_for_search_param', {
			values: { search: inputSearchValue },
		}),
		description: $format('label_check_spelling_try_search', {
			default: 'Check the spelling or try a new search.',
		}),
	};

	$: mediaPlayerRadioFiles = radioFavorites?.map((file) => ({
		id: file?.id,
		country_code: file?.country_code,
		image: file?.icon || file?.image,
		title: file?.name,
	}));

	$: mediaPlayerMediaFiles = mediaFiles?.map((file, index) => ({
		id: file?.id,
		index: index,
		title: file?.title,
		artist: file?.artist,
		image: file?.preview,
	}));
	$: audioCurrentTime = Math.floor(position / 1000);
	let radioFavorites = [];

	let isPlaying = status?.media?.playback?.enable;
	let heading =
		typeof status?.media !== 'undefined'
			? isPlaying
				? status?.media?.playback?.media_meta?.title
				: $format('heading_no_media', { default: ' No media playing' })
			: undefined;
	let loading = status?.media?.playback?.buffering && !isPlaying;
	let duration = status?.media?.playback?.media_meta?.duration;
	let position = status?.media?.playback?.media_meta?.position;
	let timer = null;
	let loadingList = false;
	let audioDuration = Math.floor(duration / 1000);
	let pinLocked =
		typeof $pin_unlocked_devices[device?.id] === 'undefined' && $devices[device?.id]?.pin_lock;
	let volumeValue = 0;
	let targetVolumeValue;
	let volumeValueCache;
	let callbackValue;
	let showModal = false;
	let togglePlayAfterUnlock = false;
	let startPlayAfterUnlock = false;
	let startPlayAfterUnlockItem = {
		file: null,
		type: null,
	};
	let playPreviousAfterUnlock = false;
	let playNextAfterUnlock = false;
	let activeButton: 1 | 2 = 1;

	const valueFromStatus = () => {
		volumeValue = percent_from_value_range(status?.media?.playback?.volume, [0, 10]);
		targetVolumeValue = volumeValue;
	};

	$: isMoving =
		typeof volumeValue !== 'undefined' &&
		typeof targetVolumeValue !== 'undefined' &&
		volumeValue !== volumeValueCache;

	$: if (
		!isMoving ||
		(typeof targetVolumeValue === 'undefined' &&
			status?.media?.playback?.volume !== targetVolumeValue)
	) {
		valueFromStatus();
	}

	$: if (status?.media?.playback?.volume === targetVolumeValue && targetVolumeValue === volumeValue) {
		targetVolumeValue = undefined;
		volumeValueCache = undefined;
	}

	$: if (heading) {
		const indexOfMediaItem = mediaPlayerMediaFiles.findIndex(
			(item) => item.title === status?.media?.playback?.media_meta?.title,
		);
		const indexOfRadioItem = mediaPlayerRadioFiles.findIndex(
			(item) => item.title === status?.media?.playback?.media_meta?.title,
		);

		if (indexOfMediaItem > -1) {
			if (
				JSON.stringify(currentSong) !== JSON.stringify(mediaPlayerMediaFiles[indexOfMediaItem])
			) {
				currentSong = mediaPlayerMediaFiles[indexOfMediaItem];
				currentRadio = null;
				activeButton = 2;
			}
		} else if (indexOfRadioItem > -1) {
			if (
				JSON.stringify(currentRadio) !== JSON.stringify(mediaPlayerRadioFiles[indexOfRadioItem])
			) {
				currentRadio = mediaPlayerRadioFiles[indexOfRadioItem];
				currentSong = null;
				activeButton = 1;
			}
		} else {
			if (status?.media?.playback.media_type === 'RADIO') {
				const newCurrentRadio = {
					title: status?.media?.playback?.media_meta?.title,
					image: $statusStore?.media?.playback?.media_meta?.thumb,
				};

				if (JSON.stringify(newCurrentRadio) !== JSON.stringify(currentRadio)) {
					currentRadio = { ...newCurrentRadio };
					currentSong = null;
					activeButton = 1;
				}
			}
		}
	}

	/**
	 * Updates the component's state before the component is updated.
	 *
	 * @description Updates the `isPlaying`, `heading`, `loading`, and `duration` variables based on the `status` object.
	 */
	beforeUpdate(() => {
		/**
		 * Whether the media is currently playing.
		 * @type {boolean}
		 */
		isPlaying = status?.media?.playback?.enable;

		/**
		 * The title of the currently playing media, or a default message if no media is playing.
		 * @type {string}
		 */
		heading =
			// isPlaying &&
			typeof status?.media?.playback?.media_meta?.title !== 'undefined'
				? status?.media?.playback?.media_meta?.title
				: ' No media playing';

		/**
		 * Whether the media is currently buffering.
		 * @type {boolean}
		 */
		loading = status?.media?.playback?.buffering && !isPlaying;

		if (duration !== status?.media?.playback?.media_meta?.duration) {
			/**
			 * The duration of the currently playing media.
			 * @type {number}
			 */
			duration = status?.media?.playback?.media_meta?.duration;
			audioDuration = Math.floor(duration / 1000);
			clearTimerInterval();
		}

		if (isPlaying && timer === null) {
			setTime();
		} else if (!isPlaying || typeof duration === 'undefined') {
			clearTimerInterval();
		}
	});

	/**
	 * Handles error responses from API requests.
	 *
	 * @param {MediaResponseInterface} res - The error response from the API.
	 * @returns {void}
	 */
	const errorResponse = (res: MediaResponseInterface) => {
		/**
		 * The error code from the API response.
		 * @type {string}
		 */
		const code = res?.errors?.code.toString();

		/**
		 * The error message from the API response.
		 * @type {string}
		 */
		// const text = errors(code, res?.errors?.message);

		toast.open({ type: 'danger', title: code, text: res?.errors?.message });
	};

	/**
	 * Retrieves the media playback status from the API.
	 *
	 * @async
	 * @returns {void}
	 */
	const getMediaStatus = async () => {
		/**
		 * The API response containing the media playback status.
		 * @type {MediaResponseInterface}
		 */
		const data = channel ? { config: { channel } } : undefined;
		const _res: MediaResponseInterface = await gen2Request(device, 'Media.GetStatus', { ...data });

		if (_res.isok) {
			/**
			 * The media playback status from the API response.
			 * @type {object}
			 */
			if (typeof _res?.data?.playback !== 'undefined') {
				status.media.playback = _res.data.playback;
			}
			/**
			 * The current position of the media playback.
			 * @type {number}
			 */
			position = status?.media?.playback?.media_meta?.position;
		}
	};
	/**
	 * Sends a request to play the previous media item.
	 *
	 * @async
	 * @function previous
	 * @returns {Promise<void>}
	 */
	const previous = async () => {
		if (!pinLocked) {
			const res: MediaResponseInterface = await gen2Request(device, 'Media.MediaPlayer.Previous', {
				config: {},
			});
			if (res.isok) {
				getMediaStatus();
			} else {
				errorResponse(res);
				getMediaStatus();
			}
		} else {
			playPreviousAfterUnlock = true;
			showModal = true;
		}
	};

	/**
	 * Sends a request to play the next media item.
	 *
	 * @async
	 * @function next
	 * @returns {Promise<void>}
	 */
	const next = async () => {
		if (!pinLocked) {
			const data = channel ? { config: { channel } } : undefined;
			const res: MediaResponseInterface = await gen2Request(device, 'Media.MediaPlayer.Next', {
				...data,
			});
			if (res.isok) {
				getMediaStatus();
			} else {
				errorResponse(res);
				getMediaStatus();
			}
		} else {
			playNextAfterUnlock = true;
			showModal = true;
		}
	};

	/**
	 * Sends a request to toggle the play/pause state of the media player.
	 *
	 * @async
	 * @function togglePlay
	 * @returns {Promise<void>}
	 */
	const togglePlay = async () => {
		if (!pinLocked) {
			const data = channel ? { config: { channel } } : undefined;
			const res: MediaResponseInterface = await gen2Request(
				device,
				'Media.MediaPlayer.PlayOrPause',
				{
					...data,
				},
			);
			if (res.isok) {
				getMediaStatus();
			} else {
				errorResponse(res);
			}
		} else {
			showModal = true;
			togglePlayAfterUnlock = true;
		}
	};

	/* - ToDo - there are more media player commands that can be added here. At the moment they are not supported by the API with response code: -114 and message: "No such service!"
	Media:
	http://IP_WD/rpc/Media.GetStatus
	http://IP_WD/rpc/Media.List
	http://IP_WD/rpc/Media.Reload
	http://IP_WD/rpc/Media.SetVolume?volume=[0...10]
	http://IP_WD/rpc/Media.Delete?id=ID_FROM_MEDIA.LIST

	Media.Radio:
	http://IP_WD/rpc/Media.Radio.ListFavourites
	http://IP_WD/rpc/Media.Radio.PlayFavourite?id=ID_FROM_RADIO.LISTFAVOURITES
	http://IP_WD/rpc/Media.Radio.PlayNextFavourite
	http://IP_WD/rpc/Media.Radio.PlayPreviousFavourite
	http://IP_WD/rpc/Media.Radio.Stop

	Media.MediaPlayer:
	http://IP_WD/rpc/Media.MediaPlayer.Next
	http://IP_WD/rpc/Media.MediaPlayer.Play
	http://IP_WD/rpc/Media.MediaPlayer.Pause
	http://IP_WD/rpc/Media.MediaPlayer.PlayOrPause
	http://IP_WD/rpc/Media.MediaPlayer.Play?id=ID_FROM_MEDIA.LIST
	http://IP_WD/rpc/Media.MediaPlayer.PlayAudioClip?id=ID_FROM_MEDIA.LIST
	http://IP_WD/rpc/Media.MediaPlayer.Previous
	http://IP_WD/rpc/Media.MediaPlayer.Stop
	*/

	/**
	 * Retrieves a list of media files from the device.
	 *
	 * @async
	 * @returns {void}
	 */
	const getMediaFiles = async () => {
		const data = channel ? { config: { channel } } : undefined;
		const res: MediaResponseInterface = await gen2Request(device, 'Media.List', {
			...data,
		});
		if (res?.isok) {
			if (res?.data?.list?.length > 0) {
				mediaFiles = res.data?.list?.filter((i) => i.type === 'AUDIO');
			}
			if (res?.list?.length > 0) {
				//local response structure
				mediaFiles = res?.list?.filter((i) => i.type === 'AUDIO');
			}
			if (typeof res?.list === 'undefined' && typeof res?.data?.list === 'undefined') {
				mediaFiles = [];
			}
			if (typeof res?.list === 'undefined' && typeof res?.data?.list === 'undefined') {
				mediaFiles = [];
			}
		} else {
			return;
		}
	};

	/**
	 * Lifecycle function that runs when the component is mounted.
	 * Initializes the component's state by fetching media files and their status.
	 *
	 * @async
	 * @returns {void}
	 */
	onMount(async () => {
		// await getMediaFiles();
		// await listRadios();
		loadingList = true;
		await getMediaStatus();
		if (isPlaying && duration) {
			setTime();
		}
		await getMediaFiles();
		await listRadios();
		loadingList = false;
	});

	/**
	 * Plays a media file on the device.
	 *
	 * @async
	 * @param {object} file - The media file to play.
	 * @param {string} type - The type of media file (e.g. 'RADIO').
	 * @returns {void}
	 */
	const play = async (file, type) => {
		if (!pinLocked) {
			const src = type !== 'RADIO' ? 'Media.MediaPlayer.Play' : 'Media.Radio.PlayFavourite';
			const res: MediaResponseInterface = await gen2Request(device, src, {
				id: file.id,
			});
			if (res.isok) {
				getMediaStatus();
			} else {
				errorResponse(res);
				getMediaStatus();
			}
		} else {
			startPlayAfterUnlock = true;
			startPlayAfterUnlockItem = {
				file,
				type,
			};
			showModal = true;
		}
	};

	//There will be more media player upload from app D. Dimitrov

	/**
	 * Lists radio favorites by making a request to the device.
	 *
	 * @async
	 * @returns {void}
	 */
	const listRadios = async () => {
		const data = channel ? { config: { channel } } : undefined;
		/**
		 * Makes a request to the device to list radio favorites.
		 *
		 * @type {MediaResponseInterface}
		 */
		const res: MediaResponseInterface = await gen2Request(device, 'Media.Radio.ListFavourites', {
			...data,
		});
		if (res?.isok) {
			/**
			 * Updates the radioFavorites variable with the received list.
			 *
			 * @type {Array}
			 */
			if (res?.data?.list?.length > 0) {
				radioFavorites = res?.data?.list;
			}
			if (res?.list?.length > 0) {
				//local response structure
				radioFavorites = res?.list;
			}
			if (typeof res?.list === 'undefined' && typeof res?.data?.list === 'undefined') {
				radioFavorites = [];
			}
			if (radioFavorites.length !== 0) {
				mediaPlayerRadioFiles = radioFavorites?.map((radio) => ({
					id: radio?.id,
					country_code: radio?.country_code,
					image: radio?.icon,
					title: radio?.name,
				}));
			}
			setRadioFavoritesCache(device.id, radioFavorites.length > 0);
		} else {
			return;
		}
	};

	/**
	 * Sets a timer to increment the position variable at regular intervals.
	 *
	 * @returns {void}
	 */
	const setTime = () => {
		/**
		 * Clears any existing timer.
		 */
		clearTimeout(timer);
		timer = 0;
		/**
		 * Sets a new timer to increment the position variable by 10 every 10 milliseconds.
		 *
		 * @type {number}
		 */
		timer = setInterval(() => {
			position += 10;
		}, 10);
	};

	/**
	 * Clears the timer interval and resets the position variable.
	 *
	 * @returns {void}
	 */
	const clearTimerInterval = () => {
		/**
		 * Clears the timer interval.
		 */
		clearInterval(timer);
		timer = null;
		/**
		 * Resets the position variable to 0.
		 */
		position = 0;
	};

	/**
	 * Lifecycle method called when the component is destroyed.
	 *
	 * @returns {void}
	 */
	onDestroy(() => {
		/**
		 * Clears the timer interval to prevent memory leaks.
		 */
		clearTimerInterval();
	});

	const handleRequest = async (volume) => {
		if (!pinLocked) {
			let res;
			targetVolumeValue = volume * 100;

			if ($os === 'IOS') {
				hapticFeedback('buttonPressed');
			}

			loading = true;

			const _value = value_from_percent_range(targetVolumeValue, [0, 10]);

			status.media.playback.volume = _value;
			res = await gen2Request(
				device,
				`Media.SetVolume`,

				{
					id: device.channel,
					volume: _value,
					channel, //here it's not nesessary to add channel
				},
			);

			if (res.isok) {
				devices_status.update_device_status({
					id: real_id(device.id),
					msg: status,
				});
			}
			targetVolumeValue = undefined;
			loading = false;
		} else {
			callbackValue = volume;
			showModal = true;
		}
	};

	const handlePin = () => {
		const _device = { ...device };
		pinLocked = unlockPin(_device, pinLocked);
		handleRequest(callbackValue);
		callbackValue = undefined;

		if (togglePlayAfterUnlock) {
			togglePlay();
			togglePlayAfterUnlock = false;
		}

		if (startPlayAfterUnlock) {
			startPlayAfterUnlock = false;
			play(startPlayAfterUnlockItem.file, startPlayAfterUnlockItem.type);
			startPlayAfterUnlockItem = {
				file: null,
				type: null,
			};
		}

		if (playPreviousAfterUnlock) {
			previous();
			playPreviousAfterUnlock = false;
		}

		if (playNextAfterUnlock) {
			next();
			playNextAfterUnlock = false;
		}
	};

	const handleClose = () => {
		// Ivo: Doing this so it can return value for volume if pinLocked is still true :)
		volumeValue = 0;
		valueFromStatus();
	};

	const onAudioItemClick = (e: CustomEvent) => {
		const audioItem = mediaFiles.find((item) => item.id === e.detail.id);
		const radioItem = radioFavorites.find((radio) => {
			return radio?.country_code === e.detail?.country_code && radio.id === e.detail.id;
		});
		const type = radioItem ? 'RADIO' : 'AUDIO';
		play(radioItem || audioItem, type);
	};
</script>

<svelte:window
	on:visibilitychange={() => {
		getMediaStatus();
	}}
/>

{#if dashWidgetVariant}
	{#if !device}
		<DashMediaPlayerWidget
			noMediaPlayingTitle={$format('label_missing_device', {
				default: 'Missing Device',
			})}
			noMediaPlayingDesc={mediaWidget?.id}
			noMediaPlayingIcon="fa-light fa-triangle-exclamation text-warning"
		/>
	{:else}
		{#key mediaPlayerRadioFiles.length || mediaPlayerMediaFiles.length}
			<DashMediaPlayerWidget
				isFocusable
				offlineTitle={!status?.online
					? $format('device_status_no_connection', {
							default: `Device is offline`,
						})
					: ''}
				noMediaPlayingTitle={$format('no_media_found', {
					default: 'No media playing.',
				})}
				noMediaPlayingDesc={$format('select_or_upload_media_title', {
					default: 'Select or upload an audio file or play a radio station.',
				})}
				listTexts={currentRadio
					? {
							title: $format('label_favorite_stations', { default: 'Favorite stations' }),
							noFileTitle: $format('label_no_favorite_stations', {
								default: 'No favorite radio stations.',
							}),
							noFileDescription: $format('label_favorite_stations', {
								default: 'Use Wall Display to add favorite radio stations.',
							}),
						}
					: {
							title: 'Up next',
							noFileTitle: 'No audio files uploaded.',
							noFileDescription: 'Upload files from the Local Web to access them here.',
						}}
				liveStr={$format('button_live_tariff')}
				radios={mediaPlayerRadioFiles}
				songs={mediaPlayerMediaFiles}
				{currentRadio}
				{currentSong}
				{isPlaying}
				{audioCurrentTime}
				audioDuration={200}
				audioLoading={loading}
				generalLoadingState={{
					loading: loadingList,
					loadingTitle: `${$format('preparing_media_label', {
						default: 'Preparing your media',
					})}...`,
				}}
				on:audio-item-click={onAudioItemClick}
				on:list-item-click={onAudioItemClick}
				on:play-btn-click={togglePlay}
				on:pause-btn-click={togglePlay}
				on:volume-change={(e) => {
					handleRequest(e.detail.audioVolume);
				}}
				on:go-back-btn-click={previous}
				on:go-forward-btn-click={next}
				on:click
				stopAudioProgressChange
			/>
		{/key}
	{/if}
{:else}
	<div class="d-flex justify-content-center mb-s">
		<Label type="secondary" value="BETA" size="xs" />
	</div>
	<Alert
		isHeadingBold
		class="mb-s"
		heading={$format('media_upload_alert_title', { default: 'Media Upload' })}
		message={$format('alert_load_audio_files_desc', {
			default:
				'To upload audio files, please go to Local Web UI or use Display to select favorite radio stations.',
		})}
		type="info"
		dismissable
	>
		<LinkWithIcon
			size="m"
			slot="bottom"
			text={$format('upload_media_msg', { default: 'Upload Media' })}
			icon="fa-light fa-arrow-up-right-from-square"
			on:click={() => openExternalLink(`http://${get_ip(status)}/#/media`)}
		/>
	</Alert>
	{#key mediaPlayerRadioFiles.length || mediaPlayerMediaFiles.length}
		<MediaPlayer
			{activeButton}
			previewBackgroundColor={$showNewLayout ? 'color-1' : undefined}
			noMediaPlayingTitle={$format('no_media_found', {
				default: 'No media playing.',
			})}
			noMediaPlayingDesc={$format('select_or_upload_media_title', {
				default: 'Select or upload an audio file or play a radio station.',
			})}
			noFilesDetails={{
				noAudioFilesTitle: $format('no_audio_files_uploaded_title', {
					default: 'No audio files uploaded.',
				}),
				noAudioFilesDetails: $format('no_audio_files_desc', {
					default: 'Upload files from the Local Web to access them here.',
				}),
				noRadioStationsTitle: $format('no_favorite_radio_stations_title', {
					default: 'No favorite radio stations.',
				}),
				noRadioStationsDetails: $format('no_favorite_radio_stations_descr', {
					default: 'Use Wall Display to add favorite radio stations.',
				}),
			}}
			btnGroupDetails={{
				btnRadioTitle: $format('label_radio', { default: 'Radio' }),
				btnRadioIcon: 'fa-solid fa-radio',
				btnAudioFilesTitle: $format('label_audio_list'),
				btnAudioFilesIcon: 'fa-solid fa-file-music',
			}}
			liveStr={$format('button_live_tariff')}
			songs={mediaPlayerMediaFiles}
			radios={mediaPlayerRadioFiles}
			bind:inputSearchValue
			{noResultsFound}
			{currentRadio}
			{currentSong}
			{isPlaying}
			{audioCurrentTime}
			{audioDuration}
			audioLoading={loading}
			generalLoadingState={{
				loading: loadingList,
				loadingTitle: `${$format('preparing_media_label', {
					default: 'Preparing your media',
				})}...`,
			}}
			audioVolume={volumeValue / 100}
			on:audio-item-click={onAudioItemClick}
			on:play-btn-click={togglePlay}
			on:pause-btn-click={togglePlay}
			on:volume-change={(e) => {
				handleRequest(e.detail.audioVolume);
			}}
			on:go-back-btn-click={previous}
			on:go-forward-btn-click={next}
			collapseTitle={$format('label_media', { default: 'Media' })}
			stopAudioProgressChange
		/>
	{/key}
{/if}

{#if showModal}
	<PinUnlock
		bind:showModal
		on:unlock={handlePin}
		on:close={handleClose}
		on:cancel={() => (showModal = false)}
	/>
{/if}
